import type { ContextReal } from "../types.real";

/** Lo que estas funciones leen de un grupo: el % que propuso el motor y el que quedó guardado. */
type GrupoConDescuento = { id: string; descuentoPct?: number; descuentoPropuesto?: number };

/**
 * Si la marca ya eligió qué ofrecer: guardó el contexto con un techo, o dijo que no trabaja con descuentos.
 * Es el espejo de `ofertaElegida` (backend, `message-ai.util.ts`), que es la regla con la que
 * `generateMessages` se niega a escribir sin oferta (`SIN_CONTEXTO`).
 */
export function ofertaElegida(contexto: unknown): contexto is Partial<ContextReal> {
  if (!contexto || typeof contexto !== "object" || Array.isArray(contexto)) return false;
  const c = contexto as Partial<ContextReal>;
  if (c.usaDescuentos === false) return true;
  return typeof c.techoPct === "number" && Number.isFinite(c.techoPct);
}

/**
 * El techo que rige: «no trabajo con descuentos» es 0, y un techo sin sentido se acota a 0..100. Espejo de
 * `techoEfectivo` (backend, `message-ai.util.ts`), que usan el armado, la edición y el envío.
 */
function techoQueRige(contexto: Partial<ContextReal>): number {
  const techoRaw = Number(contexto.techoPct);
  const techoConfig = Number.isFinite(techoRaw) ? Math.max(0, Math.min(100, Math.floor(techoRaw))) : 100;
  return contexto.usaDescuentos === false ? 0 : techoConfig;
}

/**
 * La marca no da descuentos: «no trabajo con descuentos» o el techo en 0%. Ahí, sin puntos, el mensaje no ofrece
 * nada (el cierre `sinOferta` del backend): el plan no puede prometer «Beneficio VIP» ni un cupón.
 */
export function sinDescuentos(contexto: Partial<ContextReal> | null | undefined): boolean {
  return !!contexto && techoQueRige(contexto) === 0;
}

/** El rótulo de un grupo al que no se le ofrece nada: es el mismo que pone el backend en la tabla de grupos. */
export const SIN_OFERTA = "Sin descuento";

/**
 * El % que de verdad le llega a la gente de un grupo con este contexto. Es el espejo de
 * `descuentoDelGrupo` (backend, `message-ai.util.ts`), que es el que arma el mensaje: el % que eligió la
 * marca manda sobre el del motor (sólo en los grupos que llevan descuento), el techo recorta al final, y
 * «no trabajo con descuentos» es techo 0.
 *
 * Antes el plan hacía sólo `min(descuento del grupo, techo)`: con «10%» elegido y techo 25 mostraba
 * «20% off» para Dormidos y salía 10; con los descuentos apagados mostraba «15% off» y no salía ninguno.
 * Si el backend cambia la regla, esto cambia con él (los casos de `grupos.test.ts` son los de
 * `descuento-elegido.test.ts` del backend).
 */
export function descuentoQueSale(g: GrupoConDescuento, contexto: Partial<ContextReal>): number {
  const techo = techoQueRige(contexto);
  // el del motor, no el ya recortado de una generación anterior (ver `descuentoDelGrupo`)
  const virgen = Number(g.descuentoPropuesto);
  const propuesto = Number.isFinite(virgen) ? virgen : Number(g.descuentoPct) || 0;
  // anclado: «2x1» no es un 2% (mismo criterio que `parseDescuentoElegido`)
  const m = String(contexto.descuento ?? "").trim().match(/^(\d{1,3})\s*%?$/);
  const n = m ? Number(m[1]) : 0;
  const elegido = n > 0 ? Math.min(100, Math.floor(n)) : null;
  const base = elegido !== null && propuesto > 0 ? elegido : propuesto;
  return Math.min(base, techo);
}

/**
 * Por qué el descuento que salió (`descuentoPct`, ya aplicado) es menor que el que propuso el análisis
 * (`descuentoPropuesto`), según el contexto con el que se armó el mensaje. Sigue el orden de `descuentoDelGrupo` del
 * backend: el % que eligió la marca manda sobre el del motor, y el techo recorta al final.
 *
 * Antes se decía siempre «el techo lo bajó de 20%», y con el contexto por defecto del asistente (descuento 15%, techo
 * 15) el que bajaba a los Dormidos de 20 a 15 era el % que eligió la marca: el techo no recortaba nada. Ahora:
 * - `techo`: el techo recortó lo que iba a salir (`desde`: el del motor, o el que eligió la marca si es `deLaMarca`);
 * - `elegido`: la marca eligió un % menor que el del análisis, y el techo no tocó nada;
 * - `sinDescuentos`: «no trabajo con descuentos».
 * null si no bajó, o si no se sabe (sin el % del análisis o sin el contexto): no se inventa una causa.
 */
export type BajaDescuento =
  | { causa: "techo"; desde: number; deLaMarca: boolean }
  | { causa: "elegido"; propuesto: number }
  | { causa: "sinDescuentos"; propuesto: number };

export function porQueBajoElDescuento(g: GrupoConDescuento, contexto: Partial<ContextReal> | null | undefined): BajaDescuento | null {
  const propuesto = Number(g.descuentoPropuesto);
  if (!Number.isFinite(propuesto) || !contexto) return null;
  const aplicado = Number(g.descuentoPct) || 0;
  if (contexto.usaDescuentos === false) return propuesto > aplicado ? { causa: "sinDescuentos", propuesto } : null;
  const m = String(contexto.descuento ?? "").trim().match(/^(\d{1,3})\s*%?$/);
  const n = m ? Number(m[1]) : 0;
  const deLaMarca = n > 0 && propuesto > 0;
  const base = deLaMarca ? Math.min(100, Math.floor(n)) : propuesto;
  if (aplicado < base) return { causa: "techo", desde: base, deLaMarca };
  if (aplicado < propuesto) return { causa: "elegido", propuesto };
  return null;
}

/**
 * La causa en palabras, para poner al lado de la oferta de un grupo. `paraCliente`: lo lee una persona del cliente
 * (Accesos, rol «comercio»), que no eligió el % (lo eligió el equipo de Deenex al armar la campaña): «el % que
 * elegiste» le atribuía una decisión que no tomó (QA del 08/10). Para ella, en neutro: «el descuento de la campaña».
 */
export function textoBajaDescuento(b: BajaDescuento, o: { paraCliente?: boolean } = {}): string {
  if (o.paraCliente) {
    if (b.causa === "techo") return b.deLaMarca ? `el techo lo bajó del ${b.desde}% de la campaña` : `el techo lo bajó de ${b.desde}%`;
    if (b.causa === "elegido") return `es el descuento de la campaña (el análisis proponía ${b.propuesto}%)`;
    return `la campaña va sin descuentos (el análisis proponía ${b.propuesto}%)`;
  }
  if (b.causa === "techo") return b.deLaMarca ? `el techo lo bajó del ${b.desde}% que elegiste` : `el techo lo bajó de ${b.desde}%`;
  if (b.causa === "elegido") return `es el % que elegiste (el análisis proponía ${b.propuesto}%)`;
  return `elegiste no trabajar con descuentos (el análisis proponía ${b.propuesto}%)`;
}

/** Si el motor le propuso descuento al grupo (Dormidos, En riesgo, Nuevos) o va sin descuento por decisión del producto. */
function llevaDescuento(g: GrupoConDescuento): boolean {
  const virgen = Number(g.descuentoPropuesto);
  return (Number.isFinite(virgen) ? virgen : Number(g.descuentoPct) || 0) > 0;
}

/**
 * Cómo se nombra, en el plan del wizard, el beneficio de un grupo que va sin descuento.
 *
 * «VIP» es de campeones. «Activos regulares» (H76) también va sin descuento, pero llamarlo VIP le
 * promete al dueño un trato de campeón para un grupo que, por definición, no llega a campeón. El
 * texto coincide con la oferta que el motor le pone al grupo («Beneficio sin cupón»).
 */
const BENEFICIO_SIN_DESCUENTO: Record<string, string> = { g_activos: "Beneficio sin cupón" };

/** Espejo de `MAX_PUNTOS` del backend (`message-ai.util.ts`): un tope antitipeo, no de negocio. */
const MAX_PUNTOS = 100_000;

/**
 * Los puntos que el mensaje le ofrece a cada persona de un grupo sin descuento («500 puntos», «200 estrellas»), o
 * null si no ofrece puntos. Es el espejo de `ofertaTexto` + `puntosPorPersona` + `nombrePuntos` del backend: sólo con
 * el programa prendido, la cantidad truncada y topeada, y el nombre que eligió la marca (vacío = «puntos»). Los puntos
 * van donde no hay descuento: si el grupo lleva %, gana el % (eso lo decide quien llama).
 */
export function puntosQueSalen(contexto: Partial<ContextReal> | null | undefined): string | null {
  if (!contexto?.programaPuntos) return null;
  const n = Number(contexto.puntos?.porPersona);
  if (!Number.isFinite(n) || n <= 0) return null;
  const cantidad = Math.min(MAX_PUNTOS, Math.floor(n));
  if (cantidad <= 0) return null;
  const nombre = String(contexto.puntos?.nombre || "").trim().slice(0, 20) || "puntos";
  return `${cantidad.toLocaleString("es-AR")} ${nombre}`;
}

/**
 * El chip corto de la oferta: "15% off", "500 puntos", "Beneficio VIP" o "Beneficio sin cupón", con lo que de verdad
 * sale en el mensaje: el % elegido por la marca, recortado por el techo (`descuentoQueSale`), y donde no hay %, los
 * puntos si el programa está prendido (`puntosQueSalen`). Sin esto el plan decía "20% off" para un grupo que el techo
 * de 15% va a recortar, y «Beneficio VIP» para un grupo al que el mensaje le regala 500 puntos. El plan y el mensaje
 * coinciden.
 */
export function chipOferta(g: GrupoConDescuento, contexto: Partial<ContextReal>): string {
  const d = descuentoQueSale(g, contexto);
  if (d > 0) return `${d}% off`;
  // sin descuentos ni puntos el mensaje no ofrece nada: «Beneficio VIP» era una promesa que no salía (QA del 08/10)
  return puntosQueSalen(contexto) || (sinDescuentos(contexto) ? SIN_OFERTA : BENEFICIO_SIN_DESCUENTO[g.id] || "Beneficio VIP");
}

/** Lo que dice el reporte de un grupo con descuento mientras la marca todavía no eligió la oferta. */
export const OFERTA_A_DEFINIR = "Cupón · el % lo elegís vos";

/**
 * La oferta de un grupo en el reporte del asistente (`ReporteComercial`).
 *
 * La que trae el análisis («Cupón 20% off» para Dormidos) es el % que PROPONE el motor, no el que sale: el
 * asistente arranca con techo 15 y la marca puede elegir otro %. El reporte se ve antes de elegir la oferta
 * y prometía un 20% que, con lo de siempre, salía 15. Ahora:
 * - sin la oferta elegida, un grupo con descuento dice que el % lo elige la marca (no «hasta 20%»: el %
 *   elegido manda sobre el del motor, así que 20 tampoco es un tope);
 * - con la oferta elegida (el contexto GUARDADO, el que va a usar el mensaje), el % que sale;
 * - un grupo sin descuento (Campeones, Activos) queda con el suyo: ese 0 es del producto, no de la marca.
 */
export function ofertaEnReporte(g: GrupoConDescuento & { oferta?: string }, contexto: unknown): string | undefined {
  // con la oferta elegida y el programa de puntos prendido, al que no le toca % le llegan puntos (como en el mensaje)
  const puntos = ofertaElegida(contexto) ? puntosQueSalen(contexto) : null;
  // sin descuentos (con la oferta elegida) a nadie se le ofrece nada, tampoco el «Beneficio VIP» de Campeones
  const nada = ofertaElegida(contexto) && sinDescuentos(contexto);
  if (!llevaDescuento(g)) return puntos || (nada ? SIN_OFERTA : g.oferta);
  if (!ofertaElegida(contexto)) return OFERTA_A_DEFINIR;
  const d = descuentoQueSale(g, contexto);
  // con 0 (techo 0 o descuentos apagados) es el mismo rótulo que pone `generarMensajes`: los puntos, si hay
  return d > 0 ? `Cupón ${d}% off` : puntos || SIN_OFERTA;
}

/**
 * Por qué cada grupo recibe lo que recibe, en una línea (perfil → oferta → canal). Encuadra el 2% de Nuevos como
 * POTENCIAL futuro, no como "no valen".
 *
 * Depende de lo que la marca eligió: antes era un texto fijo y, con «No trabajo con descuentos», el plan seguía
 * diciendo «va el descuento más alto», «un beneficio VIP los premia» y «un cupón de bienvenida» (QA del 08/10). Sin
 * descuentos se dice por qué se les escribe, sin prometer lo que el mensaje no da; con descuento, lo de siempre.
 */
const RAZON_GRUPO: Record<string, string> = {
  g_dormidos: "Son pocos pero valen mucho y hace rato no vienen: va el descuento más alto y por WhatsApp, el canal más directo, porque recuperar a cada uno paga.",
  g_campeones: "Ya te compran seguido: no hace falta resignar margen. Un beneficio VIP los premia sin regalar plata, por los dos canales para no fallarles.",
  g_activos: "Compraron hace poco y vuelven, aunque todavía no son campeones. Un descuento sería regalar plata a quien iba a volver igual: va un beneficio sin cupón para cuidar el vínculo.",
  g_riesgo: "Se están enfriando y la ventana es corta. Un empujón medido por WhatsApp para agarrarlos antes de que se vayan del todo.",
  g_nuevos: "Compraron una sola vez: la segunda compra define si se quedan. Un cupón de bienvenida por email para invitarlos a repetir, sin apurar.",
};
const RAZON_SIN_DESCUENTOS: Record<string, string> = {
  g_dormidos: "Son pocos pero valen mucho y hace rato no vienen: les escribimos por WhatsApp, el canal más directo, porque recuperar a cada uno paga.",
  g_campeones: "Ya te compran seguido: un mensaje que los reconozca, sin regalar plata, por los dos canales para no fallarles.",
  g_activos: "Compraron hace poco y vuelven, aunque todavía no son campeones: un mensaje para cuidar el vínculo, sin regalar plata.",
  g_riesgo: "Se están enfriando y la ventana es corta. Un mensaje por WhatsApp para agarrarlos antes de que se vayan del todo.",
  g_nuevos: "Compraron una sola vez: la segunda compra define si se quedan. Un mensaje por email para invitarlos a repetir, sin apurar.",
};
export function razonDelGrupo(id: string, contexto: Partial<ContextReal> | null | undefined): string | undefined {
  return (sinDescuentos(contexto) ? RAZON_SIN_DESCUENTOS : RAZON_GRUPO)[id];
}
